import { Button } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { FormIntegrationDto } from '@/api';
import { AddIntegrationPanel } from '@/components/AddIntegrationPanel/AddIntegrationPanel';
import { adapterName } from '@/components/Integrations/adapters/copy';
import { Section } from '@/components/Ui/Section';
import { SwitchRow } from '@/components/Ui/SwitchRow';
import { useUndoToast } from '@/components/Ui/Toast';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { listTime, shortDate } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import classes from './FormOverview.module.css';
import type { SectionProps } from './types';

const QUIET = { onSuccess: false } as const;

export function IntegrationsSection({ form, data }: Pick<SectionProps, 'form' | 'data'>) {
  const { t, i18n } = useTranslation();
  const notify = useUndoToast();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [panelOpen, setPanelOpen] = useState(false);
  const links = data.links ?? [];
  const byId = new Map((data.integrations ?? []).map((i) => [i.id, i]));
  const { api } = useRustyState.getState();

  const changed = async () => {
    await Promise.all([data.reloadLinks(), data.reloadStatus()]);
    void useRustyState.getState().refreshProblemsCount();
  };

  const label = (link: FormIntegrationDto) =>
    `${adapterName(link.kind, t)} · ${link.integration_title}`;

  const setEnabled = async (link: FormIntegrationDto, enabled: boolean) => {
    try {
      await api.updateFormIntegration(form.id, link.integration_id, { enabled }, QUIET);
      await changed();
      notify(t(enabled ? 'ui.turnedOn' : 'ui.turnedOff', { name: label(link) }), async () => {
        await api.updateFormIntegration(form.id, link.integration_id, { enabled: !enabled }, QUIET);
        await changed();
      });
    } catch {
      // the API layer reports the error
    }
  };

  const unlink = async (link: FormIntegrationDto) => {
    try {
      await api.unlinkFormIntegration(form.id, link.integration_id);
      await changed();
      notify(t('form.integrations.unlinked', { name: label(link) }), async () => {
        await api.linkFormIntegration(
          form.id,
          {
            integration_id: link.integration_id,
            event_types: link.event_types,
            enabled: link.enabled,
          },
          QUIET
        );
        await changed();
      });
    } catch {
      // the API layer reports the error
    }
  };

  const failureOf = (integrationId: string) =>
    data.problems?.find(
      (p) => p.cause === 'integration_failures' && p.integration_id === integrationId
    );

  return (
    <Section
      id="integrations"
      title={t('form.integrations.title')}
      actions={
        <Button size="xs" onClick={() => setPanelOpen(true)}>
          {t('form.integrations.add')}
        </Button>
      }
    >
      {data.links !== null && links.length === 0 && (
        <div className={classes.empty}>{t('form.integrations.none')}</div>
      )}
      {links.map((link) => {
        const failure = failureOf(link.integration_id);
        const last = byId.get(link.integration_id)?.last_delivery;
        const risk = failure
          ? [
              t('form.integrations.failed', {
                count: failure.count,
                since: failure.since ? shortDate(failure.since, i18n.language) : '',
              }),
              failure.http_status || failure.detail
                ? t('form.integrations.lastError', {
                    error: [
                      failure.http_status ? `HTTP ${failure.http_status}` : '',
                      failure.detail ?? '',
                    ]
                      .filter(Boolean)
                      .join(', '),
                  })
                : '',
            ]
              .filter(Boolean)
              .join(' ')
          : undefined;
        return (
          <SwitchRow
            key={link.integration_id}
            label={label(link)}
            checked={link.enabled}
            onChange={(next) => void setEnabled(link, next)}
            onLabel={t('ui.enabled')}
            offLabel={t('ui.switchedOff')}
            risk={risk}
            help={
              !failure && last?.outcome === 'success'
                ? t('form.integrations.lastDelivered', {
                    when: listTime(last.created_at, i18n.language),
                  })
                : undefined
            }
            action={
              <>
                <Button
                  component={Link}
                  variant="subtle"
                  size="xs"
                  to={createLanguageURL(`/account/integrations/${link.integration_id}/deliveries`)}
                >
                  {t('form.integrations.deliveries')}
                </Button>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => void unlink(link)}
                  aria-label={t('form.integrations.unlinkLabel', { name: label(link) })}
                >
                  {t('form.integrations.unlink')}
                </Button>
              </>
            }
          />
        );
      })}
      <div className={classes.footer}>
        <span className={classes.help}>{t('form.integrations.cost')}</span>
      </div>
      <AddIntegrationPanel
        opened={panelOpen}
        onClose={() => setPanelOpen(false)}
        form={{ id: form.id, title: form.title }}
        links={links}
        integrations={data.integrations}
        onChanged={changed}
      />
    </Section>
  );
}
